/**
 * Mengen-Formel-Evaluator (Sprint A.10 — 2026-05-02)
 *
 * Liest die `mengenFormel`-Strings aus `CatalogEntry.lvMapping.positions[]`
 * und evaluiert sie deterministisch gegen einen Polygon-Kontext (Wandflächen,
 * Bodenfläche, Sanitär-Stückzahlen etc.).
 *
 * **Sicherheitseigenschaften**:
 *   - Kein `eval()` und kein `Function()`-Constructor.
 *   - Eigener Recursive-Descent-Parser mit fester Grammatik.
 *   - Nur Lese-Zugriff auf den `FormelContext` über bekannte Variablen-Pfade.
 *
 * **Grammatik**:
 *   ```
 *   expression := term (("+"|"-") term)*
 *   term       := factor (("*"|"/") factor)*
 *   factor     := number
 *               | identifier
 *               | "(" expression ")"
 *               | "max" "(" expression ("," expression)+ ")"
 *               | "min" "(" expression ("," expression)+ ")"
 *               | ("-"|"+") factor
 *   identifier := word ("." word)*
 *   word       := [A-Za-z_][A-Za-z0-9_]*
 *   number     := digits ["." digits]
 *   ```
 *
 * Beispiele für valide Formeln (alle in DEFAULT_CATALOG vertreten):
 *   - `polygon.wandflaecheM2 - oeffnungenM2`
 *   - `(polygon.wandflaecheM2 - oeffnungenM2) * 2`
 *   - `polygon.wandflaecheAussenM2 - oeffnungenM2`
 *   - `min(polygon.wandflaecheInnenM2, polygon.umfangM * 2.0) - oeffnungenM2`
 *   - `polygon.bodenflaecheM2 * (1 + verschnittPct)`
 *   - `polygon.sanCount.WC`
 *   - `polygon.umfangM`
 */

// ============================================================================
// PUBLIC TYPES
// ============================================================================

/**
 * Kontext für die Evaluation einer einzelnen Position-Formel. Pro Polygon
 * (Raum) wird der `polygon`-Sub-Kontext einmal berechnet und dann iterativ
 * gegen alle codeAssignments + lvMapping-Positions evaluiert.
 *
 * `oeffnungenM2` wird pro Position separat ermittelt — bei Wand-Codes ist es
 * die Σ Öffnungen *innerhalb dieser Wand*, bei Polygon-Codes die Σ aller
 * Polygon-Öffnungen. Der Aufrufer entscheidet welcher Wert greift.
 */
export interface FormelContext {
  polygon: {
    /** Wandfläche aus Geometrie (alle Innen- und Außen-Wand-Seiten). */
    wandflaecheM2: number;
    /** Wandfläche aus Geometrie (nur die innenseitigen Putz-Sichten). */
    wandflaecheInnenM2: number;
    /** Wandfläche aus Geometrie (nur die außenseitigen Putz-Sichten). */
    wandflaecheAussenM2: number;
    /** Bodenfläche = Polygon-Fläche aus valueSet.effective.A. */
    bodenflaecheM2: number;
    /** Decken-Fläche = i.d.R. identisch zur Bodenfläche. */
    deckenflaecheM2: number;
    /** Polygon-Umfang aus valueSet.effective.L. */
    umfangM: number;
    /** Bodenfläche × Raumhöhe (für Volumen-Codes). */
    volumenM3: number;
    /** Stückzahl pro SAN-Code-Suffix (z.B. {WC: 1, WT: 1, DUSCHE: 1}).
     *  Default-Stückzahl 0 wenn der Key fehlt. */
    sanCount: Record<string, number>;
    /** Σ Türen pro Polygon (openings[type=door].length). TU-Codes nutzen das
     *  als mengenFormel "polygon.tuerCount". */
    tuerCount: number;
    /** Σ Fenster pro Polygon (openings[type=window].length). FE-Codes nutzen
     *  "polygon.fensterCount". */
    fensterCount: number;
  };
  /** Σ Öffnungen (m²) für die aktuelle Position; Caller-defined. */
  oeffnungenM2: number;
  /** Optional: Wand-Eigenschaften für Wand-Position-Formeln. */
  wand?: {
    flaecheM2: number;
    laengeM: number;
    dickeMm: number;
  };
  /** Verschnitt-Prozent als 0..1 (z.B. 0.05 = 5 %). Default 0. */
  verschnittPct: number;
}

export interface EvaluationResult {
  /** Numerisches Ergebnis (auf 3 Nachkommastellen gerundet). */
  menge: number;
  /** Klartext-Ausdruck mit eingesetzten Werten — für Audit-/Erläuterungs-UI. */
  ausdruck: string;
  /** Liste der referenzierten Variablen (für Debug + Validation). */
  referencedVariables: string[];
}

export class FormelParseError extends Error {
  constructor(
    message: string,
    public readonly formel: string,
    public readonly position: number
  ) {
    super(`${message} (in "${formel}" at ${position})`);
    this.name = "FormelParseError";
  }
}

export class FormelEvaluationError extends Error {
  constructor(
    message: string,
    public readonly formel: string,
    public readonly variable?: string
  ) {
    super(`${message}${variable ? ` (variable: "${variable}")` : ""} (in "${formel}")`);
    this.name = "FormelEvaluationError";
  }
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Evaluiert eine `mengenFormel` gegen einen `FormelContext`. Wirft
 * `FormelParseError` bei Syntax-Fehlern, `FormelEvaluationError` bei
 * unbekannten Variablen oder Division-durch-Null.
 *
 * Result.menge ist auf 3 Nachkommastellen gerundet, weil die Mengen-Tabelle
 * im Frontend ohnehin nur 2-3 Stellen zeigt und so kleine Float-Drifts
 * vermieden werden.
 */
export function evaluateMengenFormel(
  formel: string,
  ctx: FormelContext
): EvaluationResult {
  const parser = new FormelParser(formel);
  const ast = parser.parseExpression();
  parser.expectEnd();
  const referenced: Set<string> = new Set();
  const value = evaluateNode(ast, ctx, formel, referenced);
  return {
    menge: Math.round(value * 1000) / 1000,
    ausdruck: substituteValues(ast, ctx, formel),
    referencedVariables: Array.from(referenced).sort(),
  };
}

/**
 * Hilfs-Wrapper: kompletter Polygon-Aggregator pro CodeAssignment.
 *
 * @returns null wenn der Code nicht im Katalog ist oder kein lvMapping hat.
 */
export function evaluateCodeMengen(
  catalogEntry: { lvMapping?: { positions: Array<{ mengenFormel: string }> } } | undefined,
  ctx: FormelContext
): EvaluationResult[] | null {
  if (!catalogEntry?.lvMapping?.positions?.length) return null;
  return catalogEntry.lvMapping.positions.map((pos) =>
    evaluateMengenFormel(pos.mengenFormel, ctx)
  );
}

// ============================================================================
// AST + PARSER
// ============================================================================

type AstNode =
  | { type: "num"; value: number }
  | { type: "var"; path: string }
  | { type: "neg"; child: AstNode }
  | { type: "bin"; op: "+" | "-" | "*" | "/"; left: AstNode; right: AstNode }
  | { type: "fn"; name: "min" | "max"; args: AstNode[] };

class FormelParser {
  private pos = 0;
  constructor(private readonly src: string) {}

  parseExpression(): AstNode {
    let left = this.parseTerm();
    while (true) {
      this.skipWs();
      const ch = this.src[this.pos];
      if (ch === "+" || ch === "-") {
        this.pos++;
        const right = this.parseTerm();
        left = { type: "bin", op: ch, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseTerm(): AstNode {
    let left = this.parseFactor();
    while (true) {
      this.skipWs();
      const ch = this.src[this.pos];
      if (ch === "*" || ch === "/") {
        this.pos++;
        const right = this.parseFactor();
        left = { type: "bin", op: ch, left, right };
      } else {
        break;
      }
    }
    return left;
  }

  private parseFactor(): AstNode {
    this.skipWs();
    const ch = this.src[this.pos];
    // Unärer Operator
    if (ch === "-") {
      this.pos++;
      return { type: "neg", child: this.parseFactor() };
    }
    if (ch === "+") {
      this.pos++;
      return this.parseFactor();
    }
    if (ch === "(") {
      this.pos++;
      const inner = this.parseExpression();
      this.skipWs();
      if (this.src[this.pos] !== ")") {
        throw new FormelParseError("Erwartet ')'", this.src, this.pos);
      }
      this.pos++;
      return inner;
    }
    if (this.isDigit(ch) || ch === ".") {
      return this.parseNumber();
    }
    if (this.isIdentStart(ch)) {
      return this.parseIdentifierOrFn();
    }
    throw new FormelParseError(
      `Unerwartetes Zeichen '${ch ?? "<EOF>"}'`,
      this.src,
      this.pos
    );
  }

  private parseNumber(): AstNode {
    const start = this.pos;
    while (this.pos < this.src.length && this.isDigit(this.src[this.pos])) {
      this.pos++;
    }
    if (this.src[this.pos] === ".") {
      this.pos++;
      while (this.pos < this.src.length && this.isDigit(this.src[this.pos])) {
        this.pos++;
      }
    }
    const text = this.src.slice(start, this.pos);
    const value = Number(text);
    if (!Number.isFinite(value)) {
      throw new FormelParseError(`Ungültige Zahl '${text}'`, this.src, start);
    }
    return { type: "num", value };
  }

  private parseIdentifierOrFn(): AstNode {
    const start = this.pos;
    while (this.pos < this.src.length && this.isIdentChar(this.src[this.pos])) {
      this.pos++;
    }
    let path = this.src.slice(start, this.pos);
    while (this.src[this.pos] === ".") {
      this.pos++;
      const partStart = this.pos;
      if (!this.isIdentStart(this.src[this.pos])) {
        throw new FormelParseError(
          "Erwartet Bezeichner nach '.'",
          this.src,
          this.pos
        );
      }
      while (this.pos < this.src.length && this.isIdentChar(this.src[this.pos])) {
        this.pos++;
      }
      path += "." + this.src.slice(partStart, this.pos);
    }
    this.skipWs();
    // Funktion (min / max)?
    if (this.src[this.pos] === "(") {
      if (path !== "min" && path !== "max") {
        throw new FormelParseError(
          `Unbekannte Funktion '${path}' (erlaubt: min, max)`,
          this.src,
          start
        );
      }
      this.pos++; // skip "("
      const args: AstNode[] = [];
      while (true) {
        const arg = this.parseExpression();
        args.push(arg);
        this.skipWs();
        if (this.src[this.pos] === ",") {
          this.pos++;
          continue;
        }
        if (this.src[this.pos] === ")") {
          this.pos++;
          break;
        }
        throw new FormelParseError(
          "Erwartet ',' oder ')'",
          this.src,
          this.pos
        );
      }
      if (args.length < 2) {
        throw new FormelParseError(
          `${path}() braucht mindestens 2 Argumente`,
          this.src,
          start
        );
      }
      return { type: "fn", name: path as "min" | "max", args };
    }
    return { type: "var", path };
  }

  expectEnd(): void {
    this.skipWs();
    if (this.pos < this.src.length) {
      throw new FormelParseError(
        `Unerwartetes Zeichen am Ende: '${this.src[this.pos]}'`,
        this.src,
        this.pos
      );
    }
  }

  private skipWs(): void {
    while (this.pos < this.src.length && /\s/.test(this.src[this.pos])) {
      this.pos++;
    }
  }

  private isDigit(ch: string | undefined): boolean {
    return ch !== undefined && ch >= "0" && ch <= "9";
  }

  private isIdentStart(ch: string | undefined): boolean {
    if (ch === undefined) return false;
    return (
      (ch >= "a" && ch <= "z") ||
      (ch >= "A" && ch <= "Z") ||
      ch === "_"
    );
  }

  private isIdentChar(ch: string | undefined): boolean {
    return this.isIdentStart(ch) || this.isDigit(ch);
  }
}

// ============================================================================
// EVALUATOR
// ============================================================================

function evaluateNode(
  node: AstNode,
  ctx: FormelContext,
  formel: string,
  referenced: Set<string>
): number {
  switch (node.type) {
    case "num":
      return node.value;
    case "neg":
      return -evaluateNode(node.child, ctx, formel, referenced);
    case "var":
      referenced.add(node.path);
      return resolveVariable(node.path, ctx, formel);
    case "bin": {
      const a = evaluateNode(node.left, ctx, formel, referenced);
      const b = evaluateNode(node.right, ctx, formel, referenced);
      switch (node.op) {
        case "+":
          return a + b;
        case "-":
          return a - b;
        case "*":
          return a * b;
        case "/":
          if (b === 0) {
            throw new FormelEvaluationError("Division durch 0", formel);
          }
          return a / b;
      }
      // Unreachable
      throw new FormelEvaluationError(`Unbekannter Operator '${(node as { op: string }).op}'`, formel);
    }
    case "fn": {
      const vals = node.args.map((a) =>
        evaluateNode(a, ctx, formel, referenced)
      );
      return node.name === "min" ? Math.min(...vals) : Math.max(...vals);
    }
  }
}

function resolveVariable(path: string, ctx: FormelContext, formel: string): number {
  // Top-level Variablen
  if (path === "oeffnungenM2") return ctx.oeffnungenM2;
  if (path === "verschnittPct") return ctx.verschnittPct;
  // Sub-Pfade
  const parts = path.split(".");
  let cur: unknown = ctx;
  for (const part of parts) {
    if (cur === null || typeof cur !== "object") {
      throw new FormelEvaluationError(
        `Unbekannte Variable`,
        formel,
        path
      );
    }
    cur = (cur as Record<string, unknown>)[part];
  }
  if (cur === undefined || cur === null) {
    // sanCount.<UNKNOWN> → 0 (statt Fehler), das ist freundlicher für SAN-Codes.
    // Analog für tuerCount/fensterCount (für späteres Per-Type-Mapping).
    if (
      path.startsWith("polygon.sanCount.") ||
      path === "polygon.tuerCount" ||
      path === "polygon.fensterCount"
    )
      return 0;
    throw new FormelEvaluationError(`Variable nicht gesetzt`, formel, path);
  }
  if (typeof cur !== "number") {
    throw new FormelEvaluationError(`Variable ist keine Zahl`, formel, path);
  }
  return cur;
}

function substituteValues(node: AstNode, ctx: FormelContext, formel: string): string {
  switch (node.type) {
    case "num":
      return formatNumber(node.value);
    case "neg":
      return `-${substituteValues(node.child, ctx, formel)}`;
    case "var":
      try {
        return formatNumber(resolveVariable(node.path, ctx, formel));
      } catch {
        return node.path;
      }
    case "bin":
      return `(${substituteValues(node.left, ctx, formel)} ${node.op} ${substituteValues(node.right, ctx, formel)})`;
    case "fn": {
      const args = node.args.map((a) => substituteValues(a, ctx, formel)).join(", ");
      return `${node.name}(${args})`;
    }
  }
}

function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
